// Import required dependencies from React and Chart.js libraries
import React, { useRef, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * ControlChart Component
 * Displays a pie chart showing compliance percentage for a specific maturity level
 * 
 * @param {string} ml - Maturity Level (ML1, ML2, ML3)
 * @param {number} compliancePercentage - Percentage of compliance (0-100)
 */
const ControlChart = ({ ml, compliancePercentage }) => {
  // Reference to the chart instance for manual updates
  const chartRef = useRef(null);

  // Update chart data when compliance percentage changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data.datasets[0].data = [compliancePercentage, 100 - compliancePercentage];
      // Update without animation for smoother transitions
      chartRef.current.update('none');
    }
  }, [compliancePercentage]);

  // Chart data configuration
  const data = {
    labels: ['Compliant', 'Non-Compliant'],
    datasets: [
      {
        data: [compliancePercentage, 100 - compliancePercentage],
        backgroundColor: ['#4BC0C0', '#FF6384'],
        hoverBackgroundColor: ['#4BC0C0', '#FF6384'],
      },
    ],
  };

  // Update options to make chart more compact
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    animation: {
      duration: 500,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            return `${context.parsed}%`;
          }
        }
      }
    },
    layout: {
      padding: 0
    }
  };

  return (
    <div className="flex flex-col items-center mx-1"> {/* Reduced margin */}
      {/* Smaller chart container */}
      <div style={{ width: '60px', height: '60px' }}> {/* Further reduced size */}
        <Pie ref={chartRef} data={data} options={options} />
      </div>
      {/* More compact labels */}
      <div className="mt-0.5 text-center"> {/* Reduced top margin */}
        <p className="text-xs font-medium text-gray-600">{ml}</p>
        <p className="text-xs text-gray-800">{compliancePercentage.toFixed(0)}%</p>
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ControlChart);